import styled from '@emotion/styled';
import React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import CustomMonthRangePicker from '~/components/CustomMonthRangePicker';
import DropdownInput from '~/components/DropdownInput';
import Gap from '~/components/Gap';
import Text from '~/components/Text';
import { parseDateSearchParam } from '~/utils/datetime';
import useAnalyticsChartData from './hooks/useAnalyticsChartData';
import { MOBILE_QRY } from '~/config';
import { useIsDesktop } from '~/hooks/useIsDesktop';

const Container = styled.div`
  padding: 16px;
  background: #fff;
  border: 1px solid var(--border1);
  border-radius: 4px;

  ${MOBILE_QRY} {
    padding: 24px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 20px;
  flex-direction: column;
  gap: 16px;

  ${MOBILE_QRY} {
    flex-direction: row;
    gap: 0;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 20px;

  .filter-dd {
    min-width: 210px;
    max-width: 210px;
  }
`;

const analyticsOptions = [
  { label: 'Invoiced Total', value: 'invoiced' },
  { label: 'Payments Received', value: 'payments' },
];

const AnalyticsChart: React.FC = () => {
  const isDesktop = useIsDesktop();

  const { chartData, analyticsType, setAnalyticsType, dateRange, setDateRange } =
    useAnalyticsChartData();

  return (
    <Container>
      <Header>
        <Text size="smd" className="bold">
          Analytics
        </Text>
        <Filters>
          <div>
            <Text size="xs">Analytics Type</Text>
            <Gap t={4} />
            <DropdownInput
              value={analyticsType}
              options={analyticsOptions}
              onChange={newValue => setAnalyticsType(newValue as 'payments' | 'invoiced')}
              search={false}
              className="filter-dd full-width"
            />
          </div>

          {isDesktop && (
            <div>
              <Text size="xs">Date</Text>
              <Gap t={4} />
              <CustomMonthRangePicker
                onChange={(startDate, endDate) => {
                  setDateRange(`${startDate},${endDate}`);
                }}
                onClear={() => {
                  setDateRange('');
                }}
                value={parseDateSearchParam(dateRange)}
                setDefaultValue
              />
            </div>
          )}
        </Filters>
      </Header>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: isDesktop ? undefined : 14 }}
          />
          <YAxis 
            tick={{ fontSize: isDesktop ? undefined : 14 }}
          />
          <Tooltip />
          <Bar dataKey="value" fill="rgba(114,101,255,0.6)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Container>
  );
};

export default AnalyticsChart;
